"use client";

import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import { LinkButton, GetEarlyAccessButton } from "@/components/ui/buttons";
import { ease } from "@/lib/motion";

const SphereCanvas = dynamic(() => import("@/components/three/sphere-canvas"), {
  ssr: false,
});

/**
 * The three doors under the headline. They used to be three claims; since
 * 2026-08-25 they are places to go, because a visitor who has read two lines
 * about a gateway wants a key, a model list or a conversation, not a third
 * adjective. A fourth, "Browse tools", was listed as coming soon for a few
 * hours the same day and removed on instruction: a door that does not open
 * is not a door. On 2026-08-26 they became buttons, the same pair of skins
 * the PK Endpoint's hero uses (one filled, the rest outlined), so the two
 * heroes read as one family; "Browse models" was removed the same day on
 * instruction, the model cards being the next thing on the page anyway.
 */
// `href: null` means the door opens the early-access modal rather than
// navigating. Since 2026-08-30 that is what the API-key door does: it used to
// link to /dashboard/keys, which signed out is a bounce to the login page.
const DOORS: Array<{ label: string; href: string | null; primary?: boolean }> = [
  { label: "Get your API key", href: null, primary: true },
  { label: "Get dedicated NPU/GPU native inference", href: "/dedicated" },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const line = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease } },
};

/**
 * The headline's variant: it moves, it never fades.
 *
 * The h1 is this page's Largest Contentful Paint element, and `line` above
 * starts at `opacity: 0`. **An element at zero opacity is not painted as far
 * as LCP is concerned**, so the metric was running from navigation until
 * framer-motion hydrated and animated it in. PageSpeed measured that on
 * 2026-09-10 as an LCP of 4.1s on mobile against a first paint of 1.5s: 2,860
 * milliseconds of "element render delay", all of it waiting for JavaScript to
 * make visible text that was already in the HTML.
 *
 * Keeping the `y` and dropping the fade preserves the entrance and lets the
 * browser count the paint immediately. The heading is legible from the first
 * frame, which is also the better behaviour on a slow connection: the one
 * sentence explaining what this company does should not depend on a bundle.
 *
 * **Do not put `opacity` back on this variant.** If the hero's motion is ever
 * reworked, whatever element is largest in the viewport needs the same
 * treatment, or the metric regresses in exactly this way and it is invisible
 * on a fast desktop connection.
 */
const headline = {
  hidden: { y: 30 },
  show: { y: 0, transition: { duration: 0.9, ease } },
};

export function Hero() {
  return (
    <section
      id="top"
      className="relative flex min-h-[100svh] items-center overflow-hidden pt-28 pb-8 sm:pt-16"
    >
      <div className="mx-auto w-full max-w-[90rem] px-5 sm:px-8">
        <div className="grid items-center gap-4 lg:grid-cols-[1fr_1.1fr] lg:gap-6">
          {/* copy: sits above the sphere on mobile, left of it on desktop */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="max-w-2xl"
          >
            <motion.span variants={line} className="eyebrow mb-4 block">
              Pakistan&rsquo;s Sovereign AI Gateway
            </motion.span>

            {/* The headline named neither the place nor the category until
                2026-09-08: "Access leading AI models with sovereignty and
                control" is a good sentence that matches no search anyone
                performs, and an h1 is the strongest on-page signal after the
                title tag. Two words added, and the standfirst below now says
                what the product is rather than what we help with. The eyebrow
                has said "Pakistan's Sovereign AI Gateway" all along, which is
                styling, not a heading, and carries none of that weight. */}
            <motion.h1
              variants={headline}
              className="font-serif text-[clamp(2rem,3.6vw,3rem)] font-light leading-[1.08] tracking-[-0.015em]"
            >
              Access leading AI models in Pakistan, with sovereignty and control
            </motion.h1>

            <motion.p
              variants={line}
              className="mt-6 max-w-xl font-serif text-[clamp(1.15rem,1.7vw,1.4rem)] font-light leading-[1.3] tracking-[-0.01em] text-foreground/85"
            >
              One LLM gateway in front of every open-source model we serve: fast,
              cost-effective inference, billed in rupees.
            </motion.p>

            <motion.nav
              variants={line}
              aria-label="Where to start"
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              {DOORS.map((door) =>
                door.href === null ? (
                  <GetEarlyAccessButton
                    key={door.label}
                    variant={door.primary ? "primary" : "secondary"}
                    withArrow={Boolean(door.primary)}
                  >
                    {door.label}
                  </GetEarlyAccessButton>
                ) : (
                  <LinkButton
                    key={door.label}
                    href={door.href}
                    variant={door.primary ? "primary" : "secondary"}
                    withArrow={Boolean(door.primary)}
                  >
                    {door.label}
                  </LinkButton>
                ),
              )}
            </motion.nav>
            {/* The starting credit, under the buttons (2026-10-13): the growth
                audit found it invisible above the fold. Same figure the
                early-access form, sign-up and /pricing already state. */}
            <motion.p variants={line} className="mt-4 font-sans text-xs text-muted">
              Every new organisation starts with Rs 1,000 of credit.
            </motion.p>
          </motion.div>

          {/* 3D sphere: below the copy on mobile, right column on desktop.
              Restored for review on 2026-09-13. The complete mascot renderer,
              rig, source tooling and immutable v17 asset remain in the repo as
              a preserved alternate, but are intentionally not loaded here. */}
          <div className="relative h-[44vh] min-h-[300px] w-full lg:h-[88vh]">
            <div className="absolute inset-y-0 left-0 right-0 lg:-left-[6%] lg:-right-[14%] xl:-right-[20%]">
              <SphereCanvas />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
